import React, { useEffect, useRef, useState } from 'react';
import { AgGridReact, AgGridReactProps } from 'ag-grid-react';
import { toast } from 'react-hot-toast';
import { useThemeStore } from '../../state/theme';
import { updateCandidateStatus } from '../../hooks/mutations';
import { fetchJobListings } from '../../hooks/queries';
import '../../styles/Table.css';
import { Loader2 } from 'lucide-react';

interface TableProps<T> extends AgGridReactProps<T> {
  tableRef?: React.RefObject<AgGridReact<T>>;
  storageKey?: string;
  mode?: 'static' | 'server';
  datasource?: any;
}

const Table = <T extends Record<string, any>>({
  tableRef,
  storageKey = 'defaultTableState',
  mode = 'static',
  datasource,
  ...agGridProps
}: TableProps<T>) => {
  const { isDarkMode } = useThemeStore();
  const { data: jobListings } = fetchJobListings();
  const isGridReady = useRef(false);

  const [pageSize, setPageSize] = useState(() => {
    const saved = localStorage.getItem(`${storageKey}_pageSize`);
    return saved ? parseInt(saved) : (agGridProps.paginationPageSize ?? 20);
  });

  const { mutate: updateStatus } = updateCandidateStatus({
    onSuccess: (data: any) => {
      if (data.emailError) {
        toast.error(`Status updated but ${data.emailError}`);
      } else {
        toast.success('Status updated successfully');
      }
    },
    onError: (err) => toast.error(err.message),
  });

  const saveFilters = () => {
    const api = tableRef?.current?.api;
    if (api) {
      const filters = api.getFilterModel();
      localStorage.setItem(`${storageKey}_filters`, JSON.stringify(filters));
    }
  };

  const restoreFilters = () => {
    const api = tableRef?.current?.api;
    if (!api) return;

    const saved = localStorage.getItem(`${storageKey}_filters`);
    if (saved) {
      try {
        api.setFilterModel(JSON.parse(saved));
      } catch {
        localStorage.removeItem(`${storageKey}_filters`);
        console.error('Failed to parse saved filters');
      }
    }
  };

  const ensureColumnsVisible = (columnState: any[]) =>
    columnState.map((col) => ({ ...col, hide: false }));

  const saveColumnState = () => {
    if (!isGridReady.current) return;
    const api = tableRef?.current?.api;
    if (!api) return;

    const columnState = api.getColumnState();
    const visibleState = ensureColumnsVisible(columnState);
    localStorage.setItem(`${storageKey}_columns`, JSON.stringify(visibleState));
  };

  const restoreColumnState = () => {
    const api = tableRef?.current?.api;
    if (!api) return;

    const saved = localStorage.getItem(`${storageKey}_columns`);
    if (!saved) return;

    try {
      const columnState = JSON.parse(saved);
      const visibleState = ensureColumnsVisible(columnState);
      api.applyColumnState({
        state: visibleState,
        applyOrder: true,
        defaultState: { width: 150 },
      });
    } catch {
      localStorage.removeItem(`${storageKey}_columns`);
    }
  };

  const handleColumnVisible = (event: any) => {
    if (!event.visible && event.column) {
      event.api.setColumnsVisible([event.column.getId()], true);
    }
  };

  const handleGridReady = () => {
    restoreFilters();
    setTimeout(() => {
      restoreColumnState();
      isGridReady.current = true;
    }, 100);
  };

  const handleFirstDataRendered = () => {
    if (isGridReady.current) {
      setTimeout(restoreColumnState, 50);
    }
  };

  const savePaginationState = (newPageSize: number) => {
    localStorage.setItem(`${storageKey}_pageSize`, newPageSize.toString());
    setPageSize(newPageSize);
  };

  const handlePaginationChanged = () => {
    const api = tableRef?.current?.api;
    if (api && isGridReady.current) {
      const currentPageSize = api.paginationGetPageSize();
      if (currentPageSize !== pageSize) {
        savePaginationState(currentPageSize);
      }
    }
  };

  useEffect(() => {
    const handleKeyDown = (e: KeyboardEvent) => {
      const api = tableRef?.current?.api;
      if (!api) return;

      if (e.key === 'ArrowUp') api.paginationGoToPreviousPage();
      else if (e.key === 'ArrowDown') api.paginationGoToNextPage();
    };

    window.addEventListener('keydown', handleKeyDown);
    return () => window.removeEventListener('keydown', handleKeyDown);
  }, [tableRef]);

  const handleCellChange = (params: any) => {
    const { field } = params.colDef;
    const { _id, email, status, testlifyScore, jobid } = params.data;

    if (field === 'status') {
      updateStatus({ application_id: _id, email, status });
    }

    if (field === 'testlifyScore') {
      const job = jobListings?.data?.find((j: any) => j._id === jobid);
      if (job) {
        const screeningStatus =
          testlifyScore >= job.thresholdScore ? 'pass' : 'fail';
        updateStatus({
          application_id: _id,
          email,
          score: testlifyScore,
          screeningStatus,
        });
        params.node.setDataValue('screeningStatus', screeningStatus);
      }
    }
  };

  const defaultColDef = {
    resizable: true,
    sortable: true,
    filter: true,
    flex: 1,
    minWidth: 100,
  };

  return (
    <div className="flex flex-col h-full">
      <div
        className={`flex-grow w-full ${isDarkMode ? 'ag-theme-quartz-dark' : 'ag-theme-alpine'}`}
      >
        <AgGridReact
          {...agGridProps}
          ref={tableRef}
          defaultColDef={agGridProps.defaultColDef || defaultColDef}
          pagination={agGridProps.pagination ?? true}
          paginationPageSize={pageSize}
          suppressColumnVirtualisation={
            agGridProps.suppressColumnVirtualisation ?? true
          }
          domLayout={agGridProps.domLayout ?? 'normal'}
          alwaysShowHorizontalScroll={
            agGridProps.alwaysShowHorizontalScroll ?? true
          }
          maintainColumnOrder={true}
          enableCellTextSelection
          onFilterChanged={saveFilters}
          onGridReady={handleGridReady}
          onColumnMoved={saveColumnState}
          onColumnResized={saveColumnState}
          onColumnPinned={saveColumnState}
          onColumnVisible={handleColumnVisible}
          onFirstDataRendered={handleFirstDataRendered}
          onPaginationChanged={handlePaginationChanged}
          onCellValueChanged={handleCellChange}
          rowGroupPanelShow="always"
          rowData={mode === 'static' ? agGridProps.rowData : undefined}
          datasource={mode === 'server' ? datasource : undefined}
          rowModelType={mode === 'server' ? 'infinite' : undefined}
          loadingOverlayComponent={CustomLoadingOverlay}
        />
      </div>
    </div>
  );
};

const CustomLoadingOverlay = () => {
  return (
    <div className="flex items-center justify-center h-full">
      <Loader2 size={28} className="animate-spin" />
    </div>
  );
};

export default Table;
